import React, { Fragment, useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { profileSlice } from "redux/features/profile";
import {
  changesSlice,
  changesSubslice,
  updateChanges,
} from "redux/features/changes";
import { RESOURCE_TYPE_PERSON } from "redux/typeConstants";

import { Container, Message, Form, Button } from "react-bulma-components";
import { useReturn } from "util/return";
import ErrorMessage from "util/errorMessage";

import GenderComponent from "./gender";
import DisabilityComponent from "./disability";
import LgbtqEtcComponent from "./lgbtqEtc";
import PopulationGroupComponent from "./populationGroups";
import EducationLevelComponent from "./education";

import IconButton from "util/iconButton";
import { FaEdit, FaCheck, FaTimes, FaArrowLeft } from "react-icons/fa";

import { RegistrationProgress } from "../participation/registrationProgress";

const { Field, Label } = Form;

function SummaryRenderField({ label, question, value, valueLookup }) {
  const valueText =
    value === null
      ? null
      : valueLookup && valueLookup[value]
      ? valueLookup[value].label ?? null
      : value;
  const missing = valueText === null;
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      profileSlice.actions.setDemographicItemsIncomplete({ [label]: missing })
    );
  }, [dispatch, missing, label]);

  return (
    <Field horizontal className="large-horiz-label">
      <Field.Label>
        {label === null ? null : <Label>{label}</Label>}
      </Field.Label>
      <Field.Body>
        <Field>
          {valueText === null ? <i>Not specified yet</i> : valueText}
        </Field>
      </Field.Body>
    </Field>
  );
}

function FullRenderField({ label, question, valueText, children }) {
  return (
    <Field>
      <Label size="medium">{question}</Label>
      {children}
    </Field>
  );
}

function DemographicsSectionOrSummary({ forSummary }) {
  const { id, data, registrationPending, itemStatus } = useSelector(
    profileSlice.selector
  );
  const changeKeys = useMemo(() => {
    return {
      id,
      section: "demographics",
      resourceType: RESOURCE_TYPE_PERSON,
      resourceId: id,
    };
  }, [id]);

  const allChangeRecords = useSelector(changesSlice.selector);
  const changeRecord = changesSubslice(
    allChangeRecords,
    id,
    "demographics",
    RESOURCE_TYPE_PERSON,
    id
  );
  const changes = changeRecord ? changeRecord.changes : {};
  const failed = changeRecord ? changeRecord.failed : false;

  const dispatch = useDispatch();

  const doCancel = () =>
    dispatch(changesSlice.actions.deleteChangeRecord(changeKeys));

  const doClose = useReturn();

  const [closeAfterSaving, setCloseAfterSaving] = useState(false);
  useEffect(() => {
    if (closeAfterSaving && failed) setCloseAfterSaving(false);
    if (closeAfterSaving && !changeRecord) {
      doClose();
      setCloseAfterSaving(false);
    }
  }, [closeAfterSaving, failed, changeRecord, doClose]);

  const doSave = () => {
    dispatch(changesSlice.asyncActions.save(changeKeys));
    dispatch(profileSlice.actions.setDemographicItemsIncomplete(null));
    dispatch(profileSlice.actions.setDemographicsReviewed(true));
    setCloseAfterSaving(true);
  };

  const getValue = (field) =>
    changes[field] === undefined ? data[field] : changes[field];

  const setValue = (field, value, compare = undefined) => {
    updateChanges(dispatch, changeKeys, field, data[field], value, compare);
  };

  const RenderField = forSummary ? SummaryRenderField : FullRenderField;

  const forRegistration = forSummary ? null : registrationPending;
  const doCancelRegistration = () => {
    dispatch(profileSlice.actions.setRegistrationPending(null));
    doClose();
  };

  const missing = Object.values(
    itemStatus.demographics.incomplete || {}
  ).includes(true);
  const required =
    data.require_demographics || registrationPending?.require_demographics;

  return (
    <Message
      {...(forSummary && missing && required ? { color: "danger" } : {})}
    >
      <Message.Header>
        Demographic Information
        {forSummary ? null : <Button remove onClick={doClose} />}
      </Message.Header>
      <Message.Body className="demographics-summary">
        <p className="is-italic block">
          This information helps us understand the demographic background of
          participants in Fields Institute activities.{" "}
          {forSummary ? null : (
            <Fragment>
              It is used only in anonymous form to provide aggregate counts to
              our funding agencies. It helps demonstrate the Fields Institute's
              impact towards improving the representation in mathematics of
              traditionally underrepresented groups.{" "}
              <b>Each question has a "prefer not to say" option</b> that you are
              free to choose if you wish.
            </Fragment>
          )}
        </p>

        <GenderComponent {...{ getValue, setValue, forSummary, RenderField }} />
        <LgbtqEtcComponent
          {...{ getValue, setValue, forSummary, RenderField }}
        />
        <EducationLevelComponent
          {...{ orig: data, getValue, setValue, forSummary, RenderField }}
        />
        <PopulationGroupComponent
          {...{ orig: data, getValue, setValue, forSummary, RenderField }}
        />
        <DisabilityComponent
          {...{ getValue, setValue, forSummary, RenderField }}
        />

        {changeRecord && changeRecord.failed ? (
          <ErrorMessage>{changeRecord.failed.message}</ErrorMessage>
        ) : null}
        <div className="buttons">
          {forSummary ? (
            <Link to="demographics">
              <IconButton icon={FaEdit}>
                {changeRecord ? "Resume Editing" : "Edit"}
              </IconButton>
            </Link>
          ) : (
            <Fragment>
              {changeRecord || forRegistration ? (
                <IconButton icon={FaCheck} onClick={doSave}>
                  {changeRecord ? "Save Changes" : null}
                  {changeRecord && forRegistration ? " and " : null}
                  {forRegistration ? "Continue with Registration" : null}
                </IconButton>
              ) : null}
              {changeRecord ? (
                <IconButton icon={FaTimes} onClick={doCancel}>
                  Cancel Changes{" "}
                </IconButton>
              ) : forRegistration && missing && required ? (
                <IconButton icon={FaTimes} onClick={doCancelRegistration}>
                  Cancel Registration
                </IconButton>
              ) : (
                <IconButton icon={FaArrowLeft} onClick={doClose}>
                  Go Back
                </IconButton>
              )}
            </Fragment>
          )}
        </div>
      </Message.Body>
    </Message>
  );
}

export function DemographicsSummary() {
  return <DemographicsSectionOrSummary forSummary />;
}

export function DemographicsSection() {
  const { registrationPending } = useSelector(profileSlice.selector);
  return (
    <Container className="spaced">
      {registrationPending ? <RegistrationProgress /> : null}
      <DemographicsSectionOrSummary forSummary={false} />{" "}
    </Container>
  );
}

export default DemographicsSection;
